import { useRouter } from "next/router";
import { useContext, useState } from "react";
import { CSRFContext } from "src/components/csrf/csrf";
import { useConfirmSignupMutation } from "src/gql/codegen/duneGraphql";
import { AuthError } from "src/utils/api/auth";
import { frontend } from "src/utils/env/env";
import { firstQueryValue, parseBrowserQuery } from "src/utils/links/query";
import styles from "./AuthPages.module.css";
import { Form } from "src/components/ui-components/Form/Form";
import { InputField } from "src/components/ui-components/Input/InputField";
import { Button } from "src/components/ui-components/Button/Button";
import { httpPost } from "src/utils/http/http";
import { Logo } from "./Logo";

export function RegisterConfirm() {
  const [username, setUsername] = useState(
    firstQueryValue(parseBrowserQuery(), "username")
  );
  const [confirmationCode, setConfirmationCode] = useState("");
  const [error, setError] = useState<AuthError>();
  const [resending, setResending] = useState(false);

  const [confirmSignupMutation, confirmSignupResult] = useConfirmSignupMutation(
    {
      onError: (err) => setError(new AuthError(err.message)),
      onCompleted: () => {
        push(`${nextPage.pathname}${nextPage.search}`);
      },
    }
  );
  // next is the location that the user should redirect to after registering.
  const next = firstQueryValue(parseBrowserQuery(), "next");
  const { csrf } = useContext(CSRFContext);
  const { push } = useRouter();

  const nextPage = new URL("/auth/login", frontend());
  nextPage.searchParams.set("ref", "registerConfirm");
  if (next !== undefined) {
    // Propogate the post-registration redirect path to the login page.
    nextPage.searchParams.set("next", next);
  }

  const onResend = () => {
    setResending(true);
    resendConfirmationCode(csrf!, username)
      .then(() => {
        setError(undefined);
      })
      .catch((err) => {
        setResending(false);
        setError(new AuthError(err.message));
      });
  };

  const onSubmitCode = () => {
    setResending(false);
    confirmSignupMutation({
      variables: { username: username, code: confirmationCode.trim() },
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.logo}>
        <Logo />
      </div>
      <div className={styles.section}>
        <Form size="L" className={styles.form}>
          <h1 className={styles.title}>
            We've sent you an email with a confirmation code.
            <p className={styles.helperText}>
              Please use the code to confirm your email address below.
            </p>
          </h1>

          <InputField
            size="L"
            style="contained"
            label="Username or email"
            placeholder="Satoshi"
            onChange={(event) => {
              setUsername(event.target.value);
            }}
            value={username}
            required
          />
          <InputField
            size="L"
            style="contained"
            label="Confirmation code"
            placeholder="123456"
            onChange={(event) => {
              setResending(false);
              setConfirmationCode(event.target.value);
            }}
            value={confirmationCode}
            inputMode="numeric"
            autoComplete="off"
            required
          />

          {error && (
            <p role="alert" className={styles.error}>
              {error.label}
            </p>
          )}
          <Button
            size="L"
            theme="primary"
            className={styles.button}
            onClick={onSubmitCode}
            loading={confirmSignupResult.loading}
            disabled={confirmSignupResult.loading || resending}
          >
            Submit code
          </Button>
        </Form>

        <hr />

        <h2 className={styles.subtitle}>
          Didn't receive the confirmation code?
        </h2>

        <Button
          size="L"
          theme="primary-light"
          onClick={onResend}
          className={styles.button}
        >
          Resend code
        </Button>

        {resending && (
          <p className={styles.helperText}>
            Your verification email is on its way. It may take a little while to
            arrive.
          </p>
        )}
      </div>
    </div>
  );
}

const resendConfirmationCode = async (
  csrf: string,
  usernameOrEmail: string
) => {
  return httpPost<void>("/api/auth", {
    action: "resendEmailConfirmationCode",
    csrf,
    usernameOrEmail,
  });
};
